import React from "react";
import { Image as ImageIcon, Upload } from "lucide-react";

type PendingImage = {
  file: File;
  preview: string;
};

type ClientLogoUploadGridProps = {
  data?: any;
  pendingImages?: Record<string, PendingImage>;
  onSelect: (key: string, file: File) => void;
  clientsText?: string;
  listKey?: string;
};

const splitClients = (text?: string) =>
  (text || "")
    .split(/\r?\n|;/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [name] = line.split("|").map((part) => part.trim()).filter(Boolean);
      return name || line;
    });

const normalizeImage = (image?: string) => {
  if (!image) return "";
  if (image.startsWith("http") || image.startsWith("data:") || image.startsWith("blob:")) return image;
  if (image.startsWith("/")) return `https://hamdanoman.com${image}`;
  return image;
};

const ClientLogoUploadGrid: React.FC<ClientLogoUploadGridProps> = ({
  data,
  pendingImages = {},
  onSelect,
  clientsText,
  listKey = "clients_list",
}) => {
  const clients = splitClients(clientsText || data?.[listKey]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between border-b border-slate-100 pb-3">
        <h4 className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">
          Client Logos
        </h4>
        <span className="rounded-full bg-amber-50 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-amber-600">
          {clients.length} Clients
        </span>
      </div>

      {clients.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-amber-200 bg-amber-50/60 px-5 py-6 text-center text-xs font-bold leading-relaxed text-amber-700">
          Add clients in the content tab first. Logo upload boxes will appear here automatically.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {clients.map((clientName, index) => {
            const key = `clients_logo_${index + 1}`;
            const preview = normalizeImage(pendingImages[key]?.preview || data?.[key]);

            return (
              <label
                key={`${key}-${clientName}`}
                className="group relative flex aspect-[4/3] cursor-pointer items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 transition-all hover:border-amber-400 hover:bg-white"
              >
                {preview ? (
                  <img
                    src={preview}
                    alt={`${clientName} logo`}
                    className="h-full w-full object-contain p-4 transition-transform group-hover:scale-105"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2 px-3 text-center text-slate-400">
                    <ImageIcon className="h-7 w-7" />
                    <span className="text-[11px] font-black uppercase tracking-widest">
                      Upload Logo
                    </span>
                  </div>
                )}

                <div className="absolute inset-0 flex items-center justify-center bg-slate-950/0 opacity-0 transition-all group-hover:bg-slate-950/35 group-hover:opacity-100">
                  <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 text-[11px] font-black uppercase tracking-widest text-slate-700 shadow-xl">
                    <Upload className="h-4 w-4" />
                    Upload
                  </span>
                </div>

                {pendingImages[key] && (
                  <span className="absolute right-3 top-3 rounded-full bg-emerald-500 px-2 py-1 text-[10px] font-black uppercase tracking-widest text-white shadow-lg">
                    New
                  </span>
                )}

                <div className="absolute inset-x-0 bottom-0 bg-white/90 px-3 py-2 text-center text-[10px] font-black uppercase tracking-wider text-slate-500">
                  {clientName}
                </div>

                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) onSelect(key, file);
                  }}
                />
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClientLogoUploadGrid;
